/**
 * WotC Field Mapping
 *
 * Field mapping for the official Wizards of the Coast D&D 5e character sheet.
 * Field names match the fillable PDF form field IDs exactly.
 */

import type { AbilityName, SkillName } from '@character-creator/types';
import type { CharacterSheetData, PdfFieldMapping } from '../types';
import { formatModifier } from '../../../utils/formatting';

// PDF field names for abilities (from WotC template analysis)
const ABILITY_FIELD_NAMES: Record<AbilityName, { score: string; mod: string; save: string; saveCheck: string }> = {
  strength: { score: 'STR', mod: 'STRmod', save: 'ST Strength', saveCheck: 'Check Box 11' },
  dexterity: { score: 'DEX', mod: 'DEXmod ', save: 'ST Dexterity', saveCheck: 'Check Box 18' },
  constitution: { score: 'CON', mod: 'CONmod', save: 'ST Constitution', saveCheck: 'Check Box 19' },
  intelligence: { score: 'INT', mod: 'INTmod', save: 'ST Intelligence', saveCheck: 'Check Box 20' },
  wisdom: { score: 'WIS', mod: 'WISmod', save: 'ST Wisdom', saveCheck: 'Check Box 21' },
  charisma: { score: 'CHA', mod: 'CHamod', save: 'ST Charisma', saveCheck: 'Check Box 22' },
};

// PDF field names for skills (from WotC template analysis)
// The field names have inconsistent spacing - these match exactly what's in the PDF
const SKILL_FIELD_NAMES: Record<SkillName, { bonus: string; profCheck: string }> = {
  'acrobatics': { bonus: 'Acrobatics', profCheck: 'Check Box 23' },
  'animal-handling': { bonus: 'Animal', profCheck: 'Check Box 24' },
  'arcana': { bonus: 'Arcana', profCheck: 'Check Box 25' },
  'athletics': { bonus: 'Athletics', profCheck: 'Check Box 26' },
  'deception': { bonus: 'Deception ', profCheck: 'Check Box 27' },
  'history': { bonus: 'History ', profCheck: 'Check Box 28' },
  'insight': { bonus: 'Insight', profCheck: 'Check Box 29' },
  'intimidation': { bonus: 'Intimidation', profCheck: 'Check Box 30' },
  'investigation': { bonus: 'Investigation ', profCheck: 'Check Box 31' },
  'medicine': { bonus: 'Medicine', profCheck: 'Check Box 32' },
  'nature': { bonus: 'Nature', profCheck: 'Check Box 33' },
  'perception': { bonus: 'Perception ', profCheck: 'Check Box 34' },
  'performance': { bonus: 'Performance', profCheck: 'Check Box 35' },
  'persuasion': { bonus: 'Persuasion', profCheck: 'Check Box 36' },
  'religion': { bonus: 'Religion', profCheck: 'Check Box 37' },
  'sleight-of-hand': { bonus: 'SleightofHand', profCheck: 'Check Box 38' },
  'stealth': { bonus: 'Stealth ', profCheck: 'Check Box 39' },
  'survival': { bonus: 'Survival', profCheck: 'Check Box 40' },
};

export function mapWotcFields(data: CharacterSheetData): PdfFieldMapping[] {
  const mappings: PdfFieldMapping[] = [];

  // === PAGE 1: Main Stats ===

  // Basic Info
  mappings.push({ fieldName: 'CharacterName', value: data.characterName, fieldType: 'text' });
  mappings.push({ fieldName: 'ClassLevel', value: data.classLevel, fieldType: 'text' });
  mappings.push({ fieldName: 'Background', value: data.background, fieldType: 'text' });
  mappings.push({ fieldName: 'PlayerName', value: data.playerName, fieldType: 'text' });
  mappings.push({ fieldName: 'Race ', value: data.race, fieldType: 'text' }); // Note: has trailing space
  mappings.push({ fieldName: 'Alignment', value: data.alignment, fieldType: 'text' });
  mappings.push({ fieldName: 'XP', value: data.experiencePoints, fieldType: 'text' });

  // Ability Scores and Modifiers
  for (const [ability, fields] of Object.entries(ABILITY_FIELD_NAMES)) {
    const abilityName = ability as AbilityName;
    const abilityData = data.abilities[abilityName];

    // Score
    mappings.push({
      fieldName: fields.score,
      value: String(abilityData.score),
      fieldType: 'text',
    });

    // Modifier
    mappings.push({
      fieldName: fields.mod,
      value: formatModifier(abilityData.modifier),
      fieldType: 'text',
    });

    // Saving throw bonus
    const saveBonus = data.savingThrowModifiers[abilityName];
    mappings.push({
      fieldName: fields.save,
      value: formatModifier(saveBonus),
      fieldType: 'text',
    });

    // Saving throw proficiency checkbox
    const isProficient = data.savingThrowProficiencies.includes(abilityName);
    mappings.push({
      fieldName: fields.saveCheck,
      value: isProficient,
      fieldType: 'checkbox',
    });
  }

  // Proficiency Bonus
  mappings.push({
    fieldName: 'ProfBonus',
    value: formatModifier(data.proficiencyBonus),
    fieldType: 'text',
  });

  // Combat Stats
  mappings.push({ fieldName: 'AC', value: String(data.armorClass), fieldType: 'text' });
  mappings.push({ fieldName: 'Initiative', value: formatModifier(data.initiative), fieldType: 'text' });
  mappings.push({ fieldName: 'Speed', value: String(data.speed), fieldType: 'text' });
  mappings.push({ fieldName: 'HPMax', value: String(data.hitPointMaximum), fieldType: 'text' });
  mappings.push({ fieldName: 'HPCurrent', value: data.hitPointCurrent, fieldType: 'text' });
  mappings.push({ fieldName: 'HPTemp', value: data.hitPointTemp, fieldType: 'text' });
  mappings.push({ fieldName: 'HDTotal', value: data.hitDiceTotal, fieldType: 'text' });
  mappings.push({ fieldName: 'HD', value: data.hitDice, fieldType: 'text' });

  // Skills
  for (const [skill, fields] of Object.entries(SKILL_FIELD_NAMES)) {
    const skillName = skill as SkillName;
    const isProficient = data.skillProficiencies.includes(skillName);
    const bonus = data.skillModifiers[skillName];

    // Proficiency checkbox
    mappings.push({
      fieldName: fields.profCheck,
      value: isProficient,
      fieldType: 'checkbox',
    });

    // Skill bonus
    mappings.push({
      fieldName: fields.bonus,
      value: formatModifier(bonus),
      fieldType: 'text',
    });
  }

  // Passive Perception
  mappings.push({
    fieldName: 'Passive',
    value: String(data.passivePerception),
    fieldType: 'text',
  });

  // Proficiencies & Languages
  mappings.push({
    fieldName: 'ProficienciesLang',
    value: data.proficienciesAndLanguages,
    fieldType: 'text',
  });

  // Equipment
  mappings.push({
    fieldName: 'Equipment',
    value: data.equipment,
    fieldType: 'text',
  });

  // Currency
  mappings.push({ fieldName: 'CP', value: String(data.currency?.cp ?? 0), fieldType: 'text' });
  mappings.push({ fieldName: 'SP', value: String(data.currency?.sp ?? 0), fieldType: 'text' });
  mappings.push({ fieldName: 'EP', value: String(data.currency?.ep ?? 0), fieldType: 'text' });
  mappings.push({ fieldName: 'GP', value: String(data.currency?.gp ?? 0), fieldType: 'text' });
  mappings.push({ fieldName: 'PP', value: String(data.currency?.pp ?? 0), fieldType: 'text' });

  // Weapons/Attacks (up to 3)
  // Helper to format damage with type
  const formatDamageWithType = (attack: { damage: string; damageType?: string }) => {
    if (!attack.damage) return '';
    const type = attack.damageType ? ` ${attack.damageType}` : '';
    return `${attack.damage}${type}`;
  };

  if (data.attacks[0]) {
    mappings.push({ fieldName: 'Wpn Name', value: data.attacks[0].name, fieldType: 'text' });
    mappings.push({ fieldName: 'Wpn1 AtkBonus', value: data.attacks[0].bonus, fieldType: 'text' });
    mappings.push({ fieldName: 'Wpn1 Damage', value: formatDamageWithType(data.attacks[0]), fieldType: 'text' });
  }
  if (data.attacks[1]) {
    mappings.push({ fieldName: 'Wpn Name 2', value: data.attacks[1].name, fieldType: 'text' });
    mappings.push({ fieldName: 'Wpn2 AtkBonus ', value: data.attacks[1].bonus, fieldType: 'text' });
    mappings.push({ fieldName: 'Wpn2 Damage ', value: formatDamageWithType(data.attacks[1]), fieldType: 'text' });
  }
  if (data.attacks[2]) {
    mappings.push({ fieldName: 'Wpn Name 3', value: data.attacks[2].name, fieldType: 'text' });
    mappings.push({ fieldName: 'Wpn3 AtkBonus  ', value: data.attacks[2].bonus, fieldType: 'text' });
    mappings.push({ fieldName: 'Wpn3 Damage ', value: formatDamageWithType(data.attacks[2]), fieldType: 'text' });
  }

  // Features and Traits
  mappings.push({
    fieldName: 'Features and Traits',
    value: data.featuresAndTraits,
    fieldType: 'text',
  });

  // Personality
  mappings.push({ fieldName: 'PersonalityTraits ', value: data.personalityTraits, fieldType: 'text' });
  mappings.push({ fieldName: 'Ideals', value: data.ideals, fieldType: 'text' });
  mappings.push({ fieldName: 'Bonds', value: data.bonds, fieldType: 'text' });
  mappings.push({ fieldName: 'Flaws', value: data.flaws, fieldType: 'text' });

  // === PAGE 2: Backstory & Details ===

  mappings.push({ fieldName: 'CharacterName 2', value: data.characterName, fieldType: 'text' });
  mappings.push({ fieldName: 'Age', value: data.appearance.age, fieldType: 'text' });
  mappings.push({ fieldName: 'Height', value: data.appearance.height, fieldType: 'text' });
  mappings.push({ fieldName: 'Weight', value: data.appearance.weight, fieldType: 'text' });
  mappings.push({ fieldName: 'Eyes', value: data.appearance.eyes, fieldType: 'text' });
  mappings.push({ fieldName: 'Skin', value: data.appearance.skin, fieldType: 'text' });
  mappings.push({ fieldName: 'Hair', value: data.appearance.hair, fieldType: 'text' });
  mappings.push({ fieldName: 'Backstory', value: data.backstory, fieldType: 'text' });
  mappings.push({ fieldName: 'Allies', value: data.allies, fieldType: 'text' });
  mappings.push({ fieldName: 'Feat+Traits', value: data.additionalFeaturesTraits, fieldType: 'text' });
  mappings.push({ fieldName: 'Treasure', value: data.treasure, fieldType: 'text' });

  // === PAGE 3: Spellcasting ===

  if (data.spellcasting) {
    mappings.push({
      fieldName: 'Spellcasting Class 2',
      value: data.spellcasting.spellcastingClass,
      fieldType: 'text',
    });
    mappings.push({
      fieldName: 'SpellcastingAbility 2',
      value: data.spellcasting.spellcastingAbility,
      fieldType: 'text',
    });
    mappings.push({
      fieldName: 'SpellSaveDC  2',
      value: String(data.spellcasting.spellSaveDC),
      fieldType: 'text',
    });
    mappings.push({
      fieldName: 'SpellAtkBonus 2',
      value: formatModifier(data.spellcasting.spellAttackBonus),
      fieldType: 'text',
    });

    // Spell slots per level (SlotsTotal 19 = level 1, through SlotsTotal 27 = level 9)
    if (data.spellcasting.spellSlots) {
      for (let level = 1; level <= 9; level++) {
        const slotData = data.spellcasting.spellSlots[level - 1];
        if (slotData && slotData.total > 0) {
          mappings.push({
            fieldName: `SlotsTotal ${18 + level}`,
            value: String(slotData.total),
            fieldType: 'text',
          });
        }
      }
    }

    // Spell names by level
    // WotC character sheet spell field layout:
    // Cantrips (level 0): Spells 1014 - 1021 (8 slots)
    // Level 1: Spells 1022 - 1033 (12 slots)
    // Level 2: Spells 1034 - 1046 (13 slots)
    // Level 3: Spells 1047 - 1059 (13 slots)
    // Level 4: Spells 1060 - 1072 (13 slots)
    // Level 5: Spells 1073 - 1081 (9 slots)
    // Level 6: Spells 1082 - 1090 (9 slots)
    // Level 7: Spells 1091 - 1099 (9 slots)
    // Level 8: Spells 10100 - 10106 (7 slots)
    // Level 9: Spells 10107 - 101013 (7 slots)
    const spellFieldRanges = [
      { start: 1014, count: 8 },   // Level 0 (cantrips)
      { start: 1022, count: 12 },  // Level 1
      { start: 1034, count: 13 },  // Level 2
      { start: 1047, count: 13 },  // Level 3
      { start: 1060, count: 13 },  // Level 4
      { start: 1073, count: 9 },   // Level 5
      { start: 1082, count: 9 },   // Level 6
      { start: 1091, count: 9 },   // Level 7
      { start: 10100, count: 7 },  // Level 8
      { start: 10107, count: 7 },  // Level 9
    ];

    if (data.spellcasting.spellsByLevel) {
      for (let level = 0; level <= 9; level++) {
        const spells = data.spellcasting.spellsByLevel[level] || [];
        const range = spellFieldRanges[level];

        for (let i = 0; i < Math.min(spells.length, range.count); i++) {
          mappings.push({
            fieldName: `Spells ${range.start + i}`,
            value: spells[i],
            fieldType: 'text',
          });
        }
      }
    }
  }

  return mappings;
}
